import React from "react";
import type { DomainOperationResult } from "../../services/tenantApi";

/**
 * Props for DomainOperationAlert.
 * @param operations - List of domain operation results, or null to hide the component.
 * @param onClose - Callback to clear the operations state (called when user dismisses).
 */
interface DomainOperationAlertProps {
  operations: DomainOperationResult[] | null;
  onClose: () => void;
}

/**
 * Displays results of custom domain add/remove operations from the backend (CapRover).
 * Pure presentational — renders null when operations is null.
 */
export const DomainOperationAlert: React.FC<DomainOperationAlertProps> = ({
  operations,
  onClose,
}) => {
  if (operations === null) return null;

  const successCount = operations.filter((op) => op.success).length;
  const failureCount = operations.filter((op) => !op.success).length;

  return (
    <div className="space-y-2">
      <div
        className={`p-4 rounded-md ${
          failureCount === 0
            ? "bg-green-50 border border-green-200"
            : "bg-yellow-50 border border-yellow-200"
        }`}
      >
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-medium">
              {failureCount === 0
                ? "✓ Wszystkie domeny dodane pomyślnie"
                : `⚠ Częściowy sukces: ${successCount}/${operations.length}`}
            </h4>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Zamknij alert"
            className="text-neutral-400 hover:text-neutral-600 transition-colors"
          >
            ✕
          </button>
        </div>

        <div className="mt-3 space-y-2">
          {operations.map((op) => (
            <div key={op.domain} className="flex items-start space-x-2 text-sm">
              <span>{op.success ? "✓" : "✗"}</span>
              <div>
                <div className="font-mono">{op.domain}</div>
                {op.error && (
                  <div className="text-red-600 text-xs mt-1">{op.error}</div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DomainOperationAlert;
